const SIGNALS = new Set(["UP", "WAIT", "DOWN"]);
const MODES = new Set(["PRIOR_SEASON_BASELINE", "EARLY_SEASON_BLEND", "CURRENT_WITH_PRIOR_SUPPLEMENT", "CURRENT_SEASON"]);
const CLASSIFICATIONS = new Set(["MEGA SMASH", "SMASH", "LEAN OVER", "MEGA FADE", "FADE", "LEAN UNDER", "COIN TOSS"]);
const DIRECTIONS = new Set(["OVER", "UNDER"]);

export const nhlGovernedDirection = (row = {}) => {
  const direction = String(row.Direction || "").trim().toUpperCase();
  if (!DIRECTIONS.has(direction)) throw new Error("NHL governed direction is missing or invalid.");
  return direction;
};

export const nhlDisplayPropType = (value) => {
  const label = String(value || "").trim();
  return /^(?:time[\s_-]*on[\s_-]*ice|toi)(?:\s*(?:\((?:min|mins|minutes)\)|(?:min|mins|minutes)))?$/i.test(label)
    ? "TOI"
    : label;
};

export const nhlPublishedConfidencePct = (row = {}) => {
  const publishedConfidence = Number(row.Confidence);
  return Number.isFinite(publishedConfidence)
    ? Math.max(0, Math.min(100, publishedConfidence))
    : 0;
};

export const nhlEvidenceLabel = (row = {}) => {
  const mode = String(row["Evidence Mode"] || "").trim().toUpperCase();
  if (mode === "PRIOR_SEASON_BASELINE") return "Early Season · Prior-season baseline";
  if (mode === "EARLY_SEASON_BLEND") return "Early Season · Current/prior blend";
  if (mode === "CURRENT_WITH_PRIOR_SUPPLEMENT") return "Current season · Prior-season supplement";
  if (mode === "CURRENT_SEASON") return "Current-season evidence";
  return "";
};

export const nhlAnalyticsSummary = (row = {}) => {
  const estimate = Number(row.Estimate ?? row["Projected Result"] ?? row.FinalAdjustedScore);
  const edge = Number(row["Signed Edge"] ?? row.DELTA);
  const conviction = String(row.Conviction || "").trim().toUpperCase();
  if (!Number.isFinite(estimate) || !Number.isFinite(edge)) return "";
  return `Estimate ${estimate.toFixed(2)} · Edge ${edge >= 0 ? "+" : ""}${edge.toFixed(2)}${conviction ? ` · ${conviction}` : ""}`;
};

const finiteOrNull = (value) => {
  const numeric = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(numeric) ? null : numeric;
};

export const nhlMatchupPresentation = (row = {}) => {
  const market = nhlDisplayPropType(row.matchup_market ?? row["Prop Type"]);
  const semanticLabel = String(row.matchup_label || (market === "Goalie Saves" ? "OPPONENT SHOT GENERATION" : market === "Goals Allowed" ? "OPPONENT GOAL GENERATION" : `${market || "NHL"} MATCHUP`)).trim().toUpperCase();
  const canonical = canonicalMatchupPresentation(row);
  const grade = canonical.grade;
  const rank = canonical.rank;
  const leagueSize = canonical.fieldSize;
  const factor = finiteOrNull(row.matchup_factor);
  const adjustedProjection = finiteOrNull(row.matchup_adjusted_projection);
  const adjustedEdge = finiteOrNull(row.matchup_adjusted_edge);
  const sample = finiteOrNull(row.matchup_sample_games);
  const reliability = finiteOrNull(row.matchup_reliability);
  const available = canonical.available;
  const rankLabel = canonical.rankLabel;
  const compactLabel = `${semanticLabel} · ${canonical.gradeRankLabel}`;
  return {
    available, market, semanticLabel, grade, gradeLabel: canonical.gradeLabel, rank, leagueSize, fieldSize: leagueSize,
    percentile: canonical.percentile, percentileLabel: canonical.percentileLabel, rankLabel, factor,
    adjustedProjection, adjustedEdge, sample, reliability,
    seasonSample: finiteOrNull(row.matchup_season_games),
    allowedRate: finiteOrNull(row.matchup_allowed_rate),
    leagueAverage: finiteOrNull(row.matchup_league_average),
    rawFactor: finiteOrNull(row.matchup_raw_factor),
    rawProjection: finiteOrNull(row.raw_projection ?? row.Estimate),
    rawEdge: finiteOrNull(row.raw_edge ?? row["Signed Edge"]),
    opponent: String(row.matchup_opponent ?? row.Opponent ?? "").trim(),
    evidenceThrough: String(row.matchup_evidence_through || "").trim(),
    source: String(row.matchup_source || "").trim(),
    evidenceCutoff: String(row.matchup_evidence_cutoff_utc || "").trim(),
    interpretation: String(row.matchup_interpretation || "").trim(),
    unavailableReason: String(row.matchup_unavailable_reason || "").trim(),
    compactLabel,
    accessibleLabel: available
      ? `${semanticLabel.toLowerCase()} ${canonical.gradeLabel.toLowerCase()}, rank ${Math.trunc(rank)} of ${Math.trunc(leagueSize)}`
      : `${semanticLabel.toLowerCase()} unavailable`,
  };
};

export const nhlGridMatchupCells = (row = {}) => {
  const matchup = nhlMatchupPresentation(row);
  if (!matchup.available) {
    return ["Unavailable", "—", "—", "—", "—", "—", "—"];
  }
  return [
    matchup.gradeLabel,
    matchup.rankLabel,
    matchup.factor === null ? "—" : matchup.factor.toFixed(3),
    matchup.adjustedProjection === null ? "—" : matchup.adjustedProjection.toFixed(2),
    matchup.adjustedEdge === null ? "—" : `${matchup.adjustedEdge >= 0 ? "+" : ""}${matchup.adjustedEdge.toFixed(2)}`,
    matchup.sample === null ? "—" : `${matchup.sample} games`,
    matchup.reliability === null ? "—" : `${Math.round(matchup.reliability * 100)}%`,
  ];
};

export const normalizeNhlCustomerRow = (row = {}) => {
  if (String(row.Sport || "").trim().toUpperCase() !== "NHL") return row;
  const { DQS: _dqs, dqs: _derivedDqs, ...governedRow } = row;
  const signal = String(governedRow.DirectionBucket || "").trim().toUpperCase();
  const mode = String(row["Evidence Mode"] || "").trim().toUpperCase();
  if (!SIGNALS.has(signal) || !MODES.has(mode)) throw new Error("NHL customer presentation contract is invalid.");
  const direction = nhlGovernedDirection(governedRow);
  if ((direction === "OVER" && signal !== "UP") || (direction === "UNDER" && signal !== "DOWN")) {
    throw new Error("NHL governed direction conflicts with its direction bucket.");
  }
  const classification = String(row.Tag || row.Display_Tag || row.Final_Tag || "").trim().toUpperCase();
  if (classification && !CLASSIFICATIONS.has(classification)) throw new Error("NHL customer classification is invalid.");
  const sourcePropType = String(row["Prop Type"] || "").trim();
  const propType = nhlDisplayPropType(sourcePropType);
  const isToi = propType === "TOI";
  const last10 = Array.isArray(row.Last10) ? row.Last10.map((entry) => {
    if (entry && typeof entry === "object") {
      const { ["TOI (sec)"]: _toiSecondsColumn, TOI_seconds: _toiSecondsField, ...lineageEntry } = entry;
      const seconds = Number(entry["TOI (sec)"] ?? entry.TOI_seconds);
      const actual = isToi && Number.isFinite(seconds)
        ? seconds / 60
        : Number(entry.Actual ?? entry.Value ?? entry[sourcePropType] ?? entry[propType]);
      if (!Number.isFinite(actual)) return null;
      const rawMinutes = entry["TOI (min)"];
      const minutes = isToi ? actual : rawMinutes === null || rawMinutes === undefined || rawMinutes === ""
        ? Number.NaN
        : Number(rawMinutes);
      return { ...lineageEntry, Actual: actual, Value: actual, [propType]: actual,
        ...(Number.isFinite(minutes) ? { TOI: minutes, Minutes: minutes, MIN: minutes } : {}),
        ...(String(lineageEntry["Result Label"] || "").trim().toUpperCase() === "TOI (MIN)" ? { "Result Label": "TOI" } : {}) };
    }
    const value = Number(entry);
    if (!Number.isFinite(value)) return null;
    const normalized = { Value: value, [propType]: value, "Evidence Mode": mode };
    if (/^(?:time on ice|toi|minutes?)$/i.test(propType)) {
      normalized.MIN = value;
      normalized.Minutes = value;
      normalized.TOI = value;
    }
    return normalized;
  }).filter(Boolean) : [];
  const claimedSample = /\b(?:5|10)\b/.test(String(row.Explanation || ""));
  const historyStatus = last10.length ? "AVAILABLE" : claimedSample ? "CONTRACT_MISMATCH" : "UNAVAILABLE";
  return { ...governedRow, Direction: direction, ...(isToi ? { "Source Prop Type": sourcePropType, "Prop Type": "TOI" } : {}), Tag: classification || "COIN TOSS", Display_Tag: classification || "COIN TOSS", Final_Tag: classification || "COIN TOSS", Last10: last10, Analysis: String(row.Explanation || "").trim(), "Evidence Label": nhlEvidenceLabel(row), "History Evidence Status": historyStatus, "DQS Status":"UNAVAILABLE_NO_NHL_RISK_TIER" };
};

export const nhlHistoryColumns = (rows = []) => {
  const source = Array.isArray(rows) ? rows : [];
  const columns = [];
  const present = (key) => source.some((row) => row?.[key] !== null && row?.[key] !== undefined && row?.[key] !== "");
  if (present("Date")) columns.push({ key: "Date", label: "Date" });
  if (present("Matchup") || present("Opponent")) columns.push({ key: "Matchup", label: "Matchup" });
  const resultLabel = String(source.find((row) => row?.["Result Label"])?.["Result Label"] || "Result");
  if ((present("TOI") || present("Minutes") || present("MIN")) && resultLabel.toUpperCase() !== "TOI") columns.push({ key: "TOI", label: "TOI" });
  columns.push({ key: "Actual", label: resultLabel });
  if (present("Market Line")) columns.push({ key: "Market Line", label: "Line" });
  if (present("Line Result")) columns.push({ key: "Line Result", label: "Result" });
  return columns;
};

export const nhlHistoryCell = (row, key) => {
  if (key === "Matchup") return row?.Matchup || (row?.Opponent ? `vs ${row.Opponent}` : "—");
  if (key === "TOI") return row?.TOI ?? row?.Minutes ?? row?.MIN ?? "—";
  const value = row?.[key];
  return value === null || value === undefined || value === "" ? "—" : value;
};

const compactNumber = (value) => Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

export const nhlHistoryChartModel = (row = {}) => {
  const propType = String(row["Prop Type"] || "NHL result").trim();
  const line = Number(row["Prop Value"] ?? row.Line);
  const chronological = (Array.isArray(row.Last10) ? row.Last10 : []).slice(0, 10).reverse();
  const games = chronological.map((game, index) => ({
    key: String(game?.["Game ID"] || `${game?.Date || "game"}-${index}`),
    value: Number(game?.Actual ?? game?.Value ?? game?.[propType]),
    dateLabel: String(game?.Date || "—"),
    opponentLabel: String(game?.Matchup || (game?.Opponent ? `vs ${game.Opponent}` : "—")),
    title: `${game?.Date || "Date unavailable"} · ${game?.Matchup || game?.Opponent || "Matchup unavailable"}`,
  })).filter((game) => Number.isFinite(game.value));
  const over = Number.isFinite(line) ? games.filter((game) => game.value > line).length : 0;
  const under = Number.isFinite(line) ? games.filter((game) => game.value < line).length : 0;
  const average = games.length ? games.reduce((sum, game) => sum + game.value, 0) / games.length : null;
  const unit = /time on ice|\btoi\b|minutes?/i.test(propType) ? "minutes" : "";
  const signal = String(row.DirectionBucket || "").trim().toUpperCase();
  const direction = String(row.Direction || row.Recommendation || (signal === "UP" ? "OVER" : signal === "DOWN" ? "UNDER" : "")).trim().toUpperCase();
  const comparisonSummary = !Number.isFinite(line)
    ? `Last ${games.length}`
    : direction === "UNDER"
      ? `Under line ${under} of ${games.length}`
      : direction === "OVER"
        ? `Over line ${over} of ${games.length}`
        : `Above line ${over} · Below line ${under}`;
  return {
    games,
    line: Number.isFinite(line) ? line : null,
    title: `${propType} · Last ${games.length}`,
    comparisonSummary,
    direction,
    averageLabel: average === null ? "—" : `${compactNumber(average)}${unit ? ` ${unit}` : ""}`,
    formatValue: compactNumber,
  };
};

export const localNhlReviewEnabled = (location = globalThis.window?.location) => {
  const hostname = String(location?.hostname || "").toLowerCase();
  return /^(localhost|127\.0\.0\.1)$/.test(hostname) && String(location?.port || "") === "3000";
};

export const normalizeNhlCustomerPayload = (payload = {}) => ({
  ...payload,
  props: Array.isArray(payload.props) ? payload.props.map(normalizeNhlCustomerRow) : [],
  games: Array.isArray(payload.games) ? payload.games.map((game) => ({
    ...game,
    "Game ID": String(game.game_id ?? game["Game ID"] ?? ""),
    "Away Team": game.away_team ?? game["Away Team"],
    "Home Team": game.home_team ?? game["Home Team"],
    "Game Start Timestamp": game.start_time_utc ?? game["Game Start Timestamp"],
    "Projection Count": Number(game.projection_count ?? game["Projection Count"] ?? 0),
    "Line Type Counts": game.line_type_counts ?? game["Line Type Counts"] ?? {},
  })) : [],
});
import { canonicalMatchupPresentation } from "./matchupGradePresentation";
